/**
 * useWine Hook - Wrapper for Wine Store
 * 
 * Provides an interface for App.js to manage digital cellar, wine scanning, and pairing settings.
 */

import { useCallback } from 'react';
import { useWineStore } from '../stores/wineStore';
import { useUI } from './useUI';
import { WineItem } from '../types';
import { useAppContextStore } from '../stores/appContextStore';

export function useWine(currentUser: any) {
    // Actions are stable in Zustand if retrieved via granular selectors
    const storeLoadWine = useWineStore(state => state.loadWine);
    const storeHandleAddWine = useWineStore(state => state.handleAddWine);
    const storeHandleDeleteWine = useWineStore(state => state.handleDeleteWine);
    const storeHandleSaveWine = useWineStore(state => state.handleSaveWine);
    const storeHandleStockCellar = useWineStore(state => state.handleStockCellar);
    const storeHandleWineScan = useWineStore(state => state.handleWineScan);
    const storeSetNewWine = useWineStore(state => state.setNewWine);
    const storeSetEditingWine = useWineStore(state => state.setEditingWine);
    const storeSetWineDraft = useWineStore(state => state.setWineDraft);
    const storeSetWineCellar = useWineStore(state => state.setWineCellar);
    const storeRemoveFromDraft = useWineStore(state => state.removeFromDraft);

    // State subscription
    const wineCellar = useWineStore(state => state.wineCellar);
    const wineDraft = useWineStore(state => state.wineDraft);
    const editingWine = useWineStore(state => state.editingWine);
    const isSavingWine = useWineStore(state => state.isSavingWine);
    const isWineScanning = useWineStore(state => state.isWineScanning);
    const wineUpdateLoading = useWineStore(state => state.wineUpdateLoading);
    const newWine = useWineStore(state => state.newWine);
    const isAddingWine = useWineStore(state => state.isAddingWine);

    const { triggerToast, setFadingId } = useUI();

    const getToken = useCallback(async () => {
        if (!currentUser) return null;
        return await currentUser.getIdToken();
    }, [currentUser]);

    const loadWine = useCallback(async () => {
        const token = await getToken();
        if (token) await storeLoadWine(token);
    }, [getToken, storeLoadWine]);

    const handleAddWine = useCallback(async () => {
        const token = await getToken();
        if (!token) return;

        try {
            const items = await storeHandleAddWine(token);
            // Surgical hydration instead of prefetch
            useAppContextStore.getState().hydrateStores({ wine_cellar: items });
        } catch (err) {
            triggerToast("Failed to add wine.");
        }
    }, [getToken, storeHandleAddWine, triggerToast]);

    const handleDeleteWine = useCallback(async (id: number) => {
        const token = await getToken();
        if (!token) return;

        try {
            const items = await storeHandleDeleteWine(token, id);
            triggerToast("Bottle removed.");
            // Surgical hydration
            useAppContextStore.getState().hydrateStores({ wine_cellar: items });
        } catch (err) {
            triggerToast("Failed to delete wine.");
        }
    }, [getToken, storeHandleDeleteWine, triggerToast]);

    const handleSaveWine = useCallback(async (updatedData: WineItem) => {
        const token = await getToken();
        if (!token) return;

        try {
            const items = await storeHandleSaveWine(token, updatedData);
            triggerToast("Bottle saved!");
            // Surgical hydration
            useAppContextStore.getState().hydrateStores({ wine_cellar: items });
        } catch (err) {
            triggerToast("Failed to save wine.");
        }
    }, [getToken, storeHandleSaveWine, triggerToast]);

    const handleStockCellar = useCallback(async () => {
        const token = await getToken();
        if (!token) return;

        setFadingId('stocking-wine');
        try {
            const items = await storeHandleStockCellar(token);
            triggerToast("Wines added to cellar!");
            // Surgical hydration
            useAppContextStore.getState().hydrateStores({ wine_cellar: items });
        } catch (err) {
            triggerToast("Cellar commitment failed.");
        } finally {
            setFadingId(null);
        }
    }, [getToken, storeHandleStockCellar, setFadingId, triggerToast]);

    const handleWineScan = useCallback(async (file: File) => {
        const token = await getToken();
        if (!token) return;

        try {
            const count = await storeHandleWineScan(token, file);
            if (count > 0) {
                triggerToast(`Detected ${count} bottle${count > 1 ? 's' : ''}!`);
            } else {
                triggerToast("No bottles detected.");
            }
        } catch (err) {
            triggerToast("Wine scan failed.");
        }
    }, [getToken, storeHandleWineScan, triggerToast]);

    return {
        // State
        wineCellar,
        wineDraft,
        editingWine,
        isSavingWine,
        isWineScanning,
        wineUpdateLoading,
        newWine,
        isAddingWine,

        // Actions
        loadWine,
        setNewWine: storeSetNewWine,
        setEditingWine: storeSetEditingWine,
        setWineDraft: storeSetWineDraft,
        setWineCellar: storeSetWineCellar,
        handleAddWine,
        handleDeleteWine,
        handleSaveWine,
        handleStockCellar,
        handleWineScan,
        removeFromDraft: storeRemoveFromDraft
    };
}
